<template>
  <div class="relative">
    <div class="border py-1 border-gpx_gray-800 rounded-lg bg-white relative">
      <FileList
        :files="uploadedFiles"
        @remove-file="handleRemoveFile"
      />
      <FileUploadManager
        ref="fileUploadManagerRef"
        v-model="uploadedFiles"
        @files-updated="handleFilesUpdate"
        @error="handleFileError"
      />

      <AutoResizeTextarea
        v-model="text"
        :min-height="20"
        :max-height="160"
        placeholder="Ask anything here"
        @enter="send"
        classes="w-full mt-1 pl-8 pr-10 border-0"
      />
      <button
        @click="handleFileSelect"
        class="absolute left-2 bottom-3 text-gpx_gray-200 hover:text-gpx_black-500 p-1 rounded transition-colors"
        title="Attach files"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
      </button>
      <button
        @click="send"
        :disabled="isButtonDisabled"
        class="absolute right-4 bottom-2 text-gpx_gray-200 hover:text-gpx_black-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg class="w-7 h-7" fill="#E0443E" viewBox="0 0 20 20">
          <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
        </svg>
      </button>
    </div>

    <div v-if="canSelectModel" class="mt-2">
      <select
        id="ai-model-select"
        v-model="selectedModel"
        @change="handleModelSelect"
        class="form-select w-full rounded-md border border-gpx_gray-800 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gpx_gray-900 disabled:text-gpx_gray-700"
        :aria-label="'Select AI model. Currently selected: ' + (selectedModelOption?.label || 'None')"
      >
        <option value="" disabled>Select AI model</option>
        <option
          v-for="option in modelOptions"
          :key="option.value"
          :value="option.value"
          class="py-1"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <FileDropOverlay
      @files-dropped="handleFilesDropped"
    />
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { postAiDialogMessage, postAccountDialog } from '@gpx-spa/queries';
import { useStore } from 'vuex';
import { useTypingStore } from '@/stores/useTypingStore';
import { useDialogMessagesStore } from '@/stores/useDialogMessagesStore';
import AutoResizeTextarea from '../ui/AutoResizeTextarea.vue';
import { useChatStore } from '@/stores/useChatStore';
import { FileUploadManager } from '@gpx-spa/ui';
import FileList from './FileList.vue';
import FileDropOverlay from './FileDropOverlay.vue';
import { useAbility } from '@casl/vue';
import { useToast } from 'vue-toastification';
import * as event from '@/events';
import { buildScoutModels, getScoutDefaultModel } from '@gpx-spa/constants';

const store = useStore();
const accountId = store.getters['auth/accountId'];
const toast = useToast();
const text = ref('');
const fileUploadManagerRef = ref(null);
const currentFileIds = ref([]);
const uploadedFiles = ref([]);

const models = buildScoutModels();

const props = defineProps({
  dialogId: Number,
  message: String,
});

const typingStore = useTypingStore();
const dialogMessagesStore = useDialogMessagesStore();
const selectedModel = ref(getScoutDefaultModel());
const { can } = useAbility();

const canSelectModel = computed(() => can('manage', 'AiModel'));

const modelOptions = computed(() =>
  models.map(model => ({
    value: model.id,
    label: model.name
  }))
);

const selectedModelOption = computed(() =>
  modelOptions.value.find(option => option.value === selectedModel.value)
);

const isButtonDisabled = computed(() => {
  return !text.value.trim() || (props.dialogId && typingStore.isTyping(props.dialogId.toString()));
});

watch(
  () => props.message,
  (msg) => {
    if (msg) {
      text.value = msg;
    }
  },
  {
    immediate: true,
    flush: 'post',
  },
);

const postMessage = postAiDialogMessage();
const postDialog = postAccountDialog(accountId);
const emit = defineEmits(['newDialogCreated']);
const chatStore = useChatStore();

const handleFileSelect = () => {
  fileUploadManagerRef.value?.handleFileSelect();
};

const handleFilesUpdate = (data) => {
  currentFileIds.value = data.fileIds;
};

const handleFilesDropped = (files) => {
  fileUploadManagerRef.value?.handleFilesFromOverlay(files);
};

const handleFileError = (errorMessage) => {
  toast.error(errorMessage, { timeout: 5000 });
};

const handleRemoveFile = (fileId) => {
  fileUploadManagerRef.value?.handleRemoveFile(fileId);
};

const handleModelSelect = (event) => {
  selectedModel.value = event.target.value;
};

const send = () => {
  if (!text.value.trim()) return;
  if (props.dialogId && typingStore.isTyping(props.dialogId.toString())) return;

  event.trigger(event.names.SCOUT_MESSAGE_SENT);

  if (props.dialogId) {
    typingStore.startTyping(props.dialogId.toString());

    postMessage.mutate({
      dialogId: props.dialogId,
      message: text.value.trim(),
      model: selectedModel.value,
      files: currentFileIds.value,
      user_id: store.getters['auth/user'].id
    }, {
      onSuccess: (message) => {
        dialogMessagesStore.addMessage(props.dialogId, message);
        fileUploadManagerRef.value?.clearFiles();
      }
    });
  } else {
    postDialog.mutate({
      messages: [
        { role: 'user', content: [{ type: 'text', text: text.value.trim() }] }
      ],
      model: selectedModel.value,
      files: currentFileIds.value,
      user_id: store.getters['auth/user'].id
    }, {
      onSuccess: (dialog) => {
        typingStore.startTyping(dialog.id.toString());
        if (dialog.messages && dialog.messages.length > 0) {
          dialogMessagesStore.addMessages(dialog.id, dialog.messages);
        }
        chatStore.onNewDialogCreated(dialog);
        fileUploadManagerRef.value?.clearFiles();
      }
    });
  }
  text.value = '';
};
</script>

<style scoped>
textarea {
  outline: none;
}
</style>
